import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { SummaryItem } from '../../../components/summary-list/index';
import { Tooltip } from '../../../components/tooltip/index';
import { Company, useFormat } from '../../../hooks/index';
import { EventTypeBackend } from '../../../types/events.types';
import { fromBackendDateFormat } from '../../../utils/from-backend-date-format.util';
import { toNumber } from '../../../utils/to-number.util';
import { ReverseVestingSummaryRows } from '../event-form/components/index';
import { FormSchema } from '../event-form/event-form.validation';
import {
  ManageReverseVestingEvent,
  manageReverseVestingFormRows,
  manageReverseVestingSharePrice,
} from './manage-reverse-vesting.util';

export type ManageReverseVestingSummaryStepProps = {
  data: FormSchema;
  event: ManageReverseVestingEvent;
  isLoading: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSave: () => void;
};

const summaryTitleProps = {
  titleClassName: 'w-fit',
  titleWrapperClassName: 'w-[120px]',
};

const rowClassName = 'border-b-[1px] border-gray-100';

export const ManageReverseVestingSummaryStep: FC<ManageReverseVestingSummaryStepProps> = ({
  data,
  event,
  isLoading,
  onBack,
  onCancel,
  onSave,
}) => {
  const { format } = useFormat();
  const { selectedCompany: { currency } = {} } = Company.useSelected();

  const rows = manageReverseVestingFormRows(event, data);
  const sharePrice = manageReverseVestingSharePrice(event);
  const currencySymbol = currency?.symbol ?? '$';
  const isIncorporation = event.type === EventTypeBackend.COMPANY_INCORPORATION;

  const formatCurrency = (value: number) =>
    `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 7 })}`;

  const totalShares = rows.reduce((sum, row) => sum + (toNumber(row.shares) ?? 0), 0);

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
            <SummaryItem
              className={`${rowClassName} pt-0`}
              title={
                <AppFormattedMessage id={isIncorporation ? 'INCORPORATION_DATE' : 'ISSUE_DATE'} />
              }
              value={format(fromBackendDateFormat(event.date), 'dd/MM/yyyy')}
              {...summaryTitleProps}
            />
            <SummaryItem
              className={rowClassName}
              title={<AppFormattedMessage id="TOTAL_SHARES" />}
              value={totalShares.toLocaleString('en-US')}
              {...summaryTitleProps}
            />
            <SummaryItem
              className="pb-0"
              title={
                <AppFormattedMessage id={isIncorporation ? 'SHARE_PAR_VALUE' : 'SHARES_PRICE'} />
              }
              value={formatCurrency(sharePrice)}
              {...summaryTitleProps}
            />
          </div>
        </div>

        {rows.map((row, index) => (
          <div
            className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
            key={row.id ?? index}
          >
            <Tooltip childrenWrapperClassName="max-w-[500px]" content={row.stakeholder.fullName}>
              <span className="truncate px-4 text-sm font-[550] text-gray-700">
                <AppFormattedMessage id="ISSUANCE" /> - {row.stakeholder.fullName}
              </span>
            </Tooltip>
            <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
              <SummaryItem
                className={`${rowClassName} pt-0`}
                title={<AppFormattedMessage id="SHARE_CLASS" />}
                value={<Tooltip content={row.shareClass.name}>{row.shareClass.name}</Tooltip>}
                {...summaryTitleProps}
              />
              <SummaryItem
                className={rowClassName}
                title={<AppFormattedMessage id="NO_OF_SHARES" />}
                value={(toNumber(row.shares) ?? 0).toLocaleString('en-US')}
                {...summaryTitleProps}
              />
              <SummaryItem
                className={row.reverseVesting?.enabled ? rowClassName : 'pb-0'}
                title={<AppFormattedMessage id="TOTAL_CONSIDERED_PAY" />}
                value={formatCurrency(sharePrice * (toNumber(row.shares) ?? 0))}
                {...summaryTitleProps}
              />
              {row.reverseVesting?.enabled ? (
                <ReverseVestingSummaryRows
                  reverseVesting={row.reverseVesting}
                  summaryTitleProps={summaryTitleProps}
                />
              ) : (
                <SummaryItem
                  className="pb-0"
                  title={<AppFormattedMessage id="REVERSE_VESTING" />}
                  value={<AppFormattedMessage id="DISABLED" />}
                  valueClassName="text-sunbeam-600"
                  {...summaryTitleProps}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={onBack}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={onSave}
            type="button"
          >
            <AppFormattedMessage id="SAVE_CHANGES" />
          </Button>
        </div>
      </div>
    </>
  );
};
